import axios from "axios";

// Counts the locks that never reach the server.
//
// A route denial is counted at the gate — one place, no client cooperation.
// But a <LockedControl> on a nav entry or a button renders with no request
// behind it, and those are most of the locks. This is how they say so.
//
// THREE THINGS THIS HAS TO GET RIGHT, and each is a way to get a wrong number
// rather than a crash — which is worse, because a wrong number is read as a
// fact.
//
//   1. ONE IMPRESSION PER FEATURE PER PAGE-VIEW, NOT PER RENDER. React
//      re-renders a component many times for reasons that have nothing to do
//      with a human seeing it; counting renders would inflate impressions by
//      an arbitrary factor and make the click-through ratio meaningless.
//   2. BATCHED. A page with five locks must not be five requests.
//   3. FLUSHED WHEN THE PAGE GOES AWAY. A debounce alone loses the whole batch
//      when somebody clicks straight through, which is exactly the population
//      worth counting — and it fails silently, so nobody would notice the
//      numbers were low.
export const LOCK_EVENTS_PATH = "/entitlements/lock_events";

// Long enough to collect a page's locks into one request, short enough that a
// normal read of the page flushes well before the reader leaves.
export const FLUSH_DELAY_MS = 1000;

// Keyed by feature. Impressions are deduped; clicks are not — a person who
// presses twice pressed twice, and that is a real signal about a control that
// did not appear to work.
let buffer = new Map();
let seenThisPageView = new Set();
let timer = null;
let listening = false;

const entry = (feature) => {
  if (!buffer.has(feature)) buffer.set(feature, { impressions: 0, clicks: 0 });
  return buffer.get(feature);
};

const payload = () => ({
  events: Array.from(buffer.entries()).map(([feature, counts]) => ({
    feature,
    ...counts,
  })),
});

// `sendBeacon` on the way out, axios otherwise. Beacon is the only thing the
// browser guarantees to deliver once the page is going away — a normal fetch
// is cancelled mid-flight — but it cannot set a CSRF header, so it is used
// ONLY for the unload path, where losing the batch is the alternative.
const flush = ({ beacon = false } = {}) => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (buffer.size === 0) return;

  const body = payload();
  buffer = new Map();

  if (beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(
        LOCK_EVENTS_PATH,
        new Blob([JSON.stringify(body)], { type: "application/json" }),
      );
      return;
    } catch {
      // Fall through to axios — a failed beacon is not worth losing the batch.
    }
  }

  // Fire and forget. The caller is a render path and has nothing to do with a
  // failure; an unhandled rejection here would surface as a console error on a
  // page that is working perfectly.
  axios.post(LOCK_EVENTS_PATH, body).catch(() => {});
};

// Registered once, lazily — so a page with no locks adds no listeners.
//
// `pagehide` AND `visibilitychange`, because neither alone covers everybody:
// iOS Safari frequently never fires `pagehide` for a backgrounded tab, and
// `visibilitychange` does not fire on a same-document navigation. `beforeunload`
// is deliberately not used — it is unreliable on mobile and can inhibit the
// back/forward cache.
const listen = () => {
  if (listening || typeof window === "undefined") return;
  listening = true;

  window.addEventListener("pagehide", () => flush({ beacon: true }));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush({ beacon: true });
  });
};

const schedule = () => {
  listen();
  if (timer) return;
  timer = setTimeout(() => flush(), FLUSH_DELAY_MS);
};

// A lock was put in front of somebody. Deduped per page-view: the second and
// every later call for the same feature is a no-op until the page changes.
export const recordLockImpression = (feature) => {
  if (!feature || seenThisPageView.has(feature)) return;

  seenThisPageView.add(feature);
  entry(feature).impressions += 1;
  schedule();
};

// They acted on one — opened the explainer, or pressed the call to action.
// Never deduped.
export const recordLockClick = (feature) => {
  if (!feature) return;

  entry(feature).clicks += 1;
  schedule();
};

// Inertia replaces the page without a document load, so nothing would
// otherwise clear the per-page-view dedupe and the same lock on the next page
// would go uncounted. Flushes first: the counts belong to the page that is
// ending, and the buffer is keyed by feature, not by page.
export const startLockTelemetryPageView = () => {
  flush();
  seenThisPageView = new Set();
};

// Test seam. Specs share a module instance, so one example's buffer would
// otherwise leak into the next and read as double-counting.
export const resetLockTelemetry = () => {
  if (timer) clearTimeout(timer);
  timer = null;
  buffer = new Map();
  seenThisPageView = new Set();
};
